import { slugify } from '@pkg/shared/utils/string.utils'

export interface SelectFileParams {
  type?: 'file'
  accept?: string
}

/**
 * Parameters for file upload operations
 */
export interface SelectFileResult {
  /**
   * The file to upload (optional if using handleFileSelection)
   */
  file: File
}

/**
 * Creates a file input element and returns it
 * @returns HTMLInputElement configured for file selection
 */
export function createFileInput(params: SelectFileParams = {}): HTMLInputElement {
  const fileInput = document.createElement('input')
  fileInput.type = params.type ?? 'file'
  if (params.accept) {
    fileInput.accept = params.accept
  }
  fileInput.style.display = 'none'
  document.body.appendChild(fileInput)
  return fileInput
}

/**
 * Handles the file selection process#
 * @param params - Parameters for file selection
 * @param onFileSelected - Callback when a file is selected
 * @param onCanceled - Callback when selection is canceled
 */
export function handleFileSelection(
  params: SelectFileParams = {},
  onFileSelected: (params: SelectFileResult) => void,
  onCanceled: () => void,
): void {
  // Create a hidden file input element
  const fileInput = createFileInput(params)

  // Handle the case when dialog is closed without selecting a file
  // This is needed because onchange doesn't fire when dialog is canceled
  const handleFocusBack = () => {
    // Remove the focus listener after it fires once
    window.removeEventListener('focus', handleFocusBack)

    // Small delay to allow onchange to fire first if a file was selected
    setTimeout(() => {
      // If the input is still in the DOM, it means onchange didn't fire
      if (document.body.contains(fileInput)) {
        document.body.removeChild(fileInput)
        onCanceled()
      }
    }, 300)
  }

  // Handle file selection
  fileInput.onchange = event => {
    window.removeEventListener('focus', handleFocusBack)
    const file = (event.target as HTMLInputElement).files?.[0]
    if (file) {
      // Call the callback with the selected file and relationId
      onFileSelected({
        file,
      })
    } else {
      // No file was selected, might be a cancel
      onCanceled()
    }

    // Clean up
    document.body.removeChild(fileInput)
  }

  // Add focus event to detect when dialog is closed
  window.addEventListener('focus', handleFocusBack)

  // Trigger file selection
  fileInput.click()
}

/**
 * Waits for a file to be selected by the user
 * @returns Promise that resolves with the selected file parameters
 */
export function waitForFileSelection(params: SelectFileParams = {}): Promise<SelectFileResult> {
  return new Promise<SelectFileResult>((resolve, reject) => {
    // Handle file selection with callbacks for both selection and cancellation
    handleFileSelection(
      params,
      // On file selected
      result => {
        resolve(result)
      },
      // On canceled
      () => {
        // Reject the promise if the user cancels the file selection
        reject(new Error('File selection was canceled'))
      },
    )

    // Add a timeout as a fallback to ensure the promise always resolves
    setTimeout(() => {
      reject(new Error('File selection timed out'))
    }, 60000) // 1 minute timeout as a safety measure
  })
}

export function dataURItoBlob(uri: string) {
  return new Promise<Blob>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.onload = () => {
      resolve(xhr.response)
    }
    xhr.onerror = () => {
      reject(new TypeError('Network request failed'))
    }
    xhr.responseType = 'blob'
    xhr.open('GET', uri, true)
    xhr.send(null)
  })
}

export const encodeFileAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve((reader.result as string).split(',')[1] as string)
    reader.onerror = error => reject(error)
  })

export function contentToBlob(content: string, _type = 'text/plain') {
  return fetch(content).then(res => res.blob())
}

export const normalizeFileName = (fileName: string, replaceValue = '-') => {
  return fileName.replaceAll(/[/\\?%*:|"<>]/g, replaceValue)
}

export interface SaveFileOptions {
  fileName: string
  blob: Blob
}

export function saveFile(opts: SaveFileOptions) {
  const { blob, fileName } = opts
  const a = document.createElement('a')
  document.body.appendChild(a)
  const url = window.URL.createObjectURL(blob)
  a.href = url
  a.download = fileName
  a.target = '_blank'
  a.click()
  setTimeout(() => {
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }, 0)
}

export function openFile(opts: { blob: Blob }) {
  const { blob } = opts
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(
    () => {
      URL.revokeObjectURL(url)
    },
    1000 * 60 * 5,
  ) // Clean up after 5 minutes
}

export interface DocumentData {
  fileName: string
  contentType?: string | null
  document?: string | null
}

/**
 * Previews a document in a new tab
 * @param documentData The document data containing fileName, contentType, and document content
 */
export function previewDocument(documentData: DocumentData) {
  const { fileName, contentType, document: content } = documentData

  if (!content) {
    return
  }

  const preview = window.open('')

  if (!preview) {
    return
  }

  preview.document.write(
    `<html lang="de"><head><title>${fileName}</title><style>body{margin: 0;}</style></head></html>`,
  )

  if (contentType === 'application/pdf') {
    preview.document.write(
      `<body><embed width='100%' height='100%' src='data:${contentType};base64, ${encodeURI(content)}#toolbar=0&navpanes=0&scrollbar=0'></embed></body></html>`,
    )
  } else {
    preview.document.write(
      `<body><img alt="${fileName}" width='100%' height='100%' src='data:${contentType};base64, ${content}' /></body></html>`,
    )
  }

  preview.document.close()
}

/**
 * Downloads a document
 * @param documentData The document data containing fileName, contentType, and document content
 */
export function downloadDocument(documentData: DocumentData) {
  const { fileName, contentType, document: content } = documentData

  if (!content) {
    return
  }

  const a = document.createElement('a')
  a.href = `data:${contentType};base64,${content}`
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export function slugifyFilePath(parts: string[]) {
  return parts
    .map(p => slugify(p))
    .filter(Boolean)
    .join('__')
}

export type ContentDispositionType = 'inline' | 'attachment'

/**
 * Build a `Content-Disposition` header value that survives non-ASCII filenames (umlauts) by
 * pairing an ASCII fallback with an RFC 5987 `filename*` field — both required for broad browser
 * support. Used for S3 `response-content-disposition` so downloads keep their original name even
 * though the object key is extensionless.
 */
export function contentDisposition(fileName: string, type: ContentDispositionType): string {
  const asciiFallback = fileName.replace(/[^\x20-\x7E]/g, '_').replace(/"/g, "'")
  return `${type}; filename="${asciiFallback}"; filename*=UTF-8''${encodeURIComponent(fileName)}`
}
